import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { DocumentVersionDetails } from "~/api/types";
import { updateDocumentVersion } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { DialogFooter } from "~/atoms/dialog";
import { Form } from "~/atoms/form";
import { useDocumentVersionEditForm } from "~/hooks/useDocumentVersionEditForm";
import { DocumentVersionReviewDateFields } from "~/molecules/document-fields/DocumentVersionReviewDateFields";
import type { VersionEditableField } from "~/helpers/libraryTypes";

interface DocumentVersionFieldEditFormProps {
  documentVersion: DocumentVersionDetails;
  field: VersionEditableField;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

export const DocumentVersionFieldEditForm = ({
  documentVersion,
  field,
  onCancel,
  onSaved,
}: DocumentVersionFieldEditFormProps) => {
  const { t } = useTranslation();
  const { form, filterUnchangedValues } = useDocumentVersionEditForm({
    documentVersion,
    field,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = form.handleSubmit(
    async (values) => {
      const payload = filterUnchangedValues(values);
      if (Object.keys(payload).length === 0) {
        onCancel();
        return;
      }

      try {
        setIsSubmitting(true);
        await updateDocumentVersion(documentVersion.id, payload);
        toast.success(t("toasts.success.documentUpdated"));
        await onSaved();
        onCancel();
      } catch {
        toast.error(t("toasts.error.generic"));
      } finally {
        setIsSubmitting(false);
      }
    },
    (errors) => {
      const firstError = Object.values(errors).find((error) => error?.message);
      if (firstError?.message) {
        toast.error(firstError.message);
      } else {
        toast.error(t("toasts.error.generic"));
      }
    },
  );

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-2">
        <DocumentVersionReviewDateFields field={field} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("commons.buttons.cancel")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {t("commons.buttons.save")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
